import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import type { CustomerMediaType } from '@/api/types'
import type { ApiError } from '@/lib/problem-details'

import { customerMediaBlobQueryOptions } from './queries'

/**
 * Authenticated image fetching.
 *
 * A specimen is not a public URL — `GET /customers/{id}/photo|signature|thumbprint` is behind
 * `customers.media.view` like everything else about a customer, so it has to go through
 * `apiClient` (bearer token, branch header, refresh-and-retry) rather than sit in an `<img src>`
 * an unauthenticated browser tab could also load. This fetches the bytes as a blob and turns them
 * into an object URL for exactly as long as something is displaying it — the URL is revoked the
 * moment the query's data changes or the component unmounts, matching `download.ts`'s
 * `saveBlobAsFile` for the same reason: an object URL that outlives its use is a small, silent
 * memory leak.
 */
export interface CustomerMediaBlobState {
  /** An object URL for the current specimen. Null while loading, absent, or errored. */
  url: string | null
  isPending: boolean
  /** The ordinary case for a file onboarded before capture existed — not an error to alarm over. */
  notCaptured: boolean
  /** Set only for a genuine failure. Never set alongside `notCaptured`. */
  error: ApiError | null
  refetch: () => void
}

export function useCustomerMediaBlob(
  customerId: string,
  mediaType: CustomerMediaType,
  enabled = true,
): CustomerMediaBlobState {
  const query = useQuery(customerMediaBlobQueryOptions(customerId, mediaType, enabled))
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!query.data) {
      setUrl(null)
      return
    }

    const objectUrl = URL.createObjectURL(query.data)
    setUrl(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [query.data])

  // A 404 here means "no specimen of this kind has been captured" — the ordinary answer, not a
  // failure. `getCustomerMediaBlob`'s own note explains why the dotted error code cannot be read
  // off a blob-shaped response, so the status code is what this branches on instead.
  const notCaptured = query.isError && query.error.status === 404

  return {
    url,
    isPending: enabled && (query.isPending || query.isFetching) && !query.isError,
    notCaptured,
    error: notCaptured ? null : query.isError ? query.error : null,
    refetch: () => void query.refetch(),
  }
}
